// frontend/src/components/asset/ReportAccidentModal.js
/**
 * 3-step accident report wizard for drivers (employee theme) and custodians
 * (admin theme). Creates the accident record first so the insurer clock starts
 * immediately, then uploads scene photos in a separate request.
 *
 * Steps:
 *   1. Facts — vehicle, datetime, location, type, description, injury triggers
 *   2. Photos — up to 15 scene photos (skippable)
 *   3. Third party — optional other-party particulars (skippable)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, AlertTriangle, Camera, Car, CheckCircle, FileText,
  MapPin, Navigation, Users,
} from 'lucide-react';
import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import SearchableCombobox from '../UI/SearchableCombobox';
import DatePickerField from '../UI/DatePickerField';
import PillToggle from '../UI/PillToggle';
import {
  ReturnPhotoUploader,
  validateReturnPhotosForSubmit,
} from './PendingReturnsShared';
import toast from '../../utils/toast';
import GeocodingService from '../../services/geocoding';
import { DATE_PICKER_DATETIME_FORMAT } from '../../utils/dateUtils';
import useCurrentPosition from '../../hooks/useCurrentPosition';
import { useFleetVehicles } from '../../hooks/useBIKData';
import {
  useDerivePoliceRequired,
  useReportAccident,
  useReportMyAccident,
  useAddAccidentPhotos,
  useUploadMyAccidentPhotos,
} from '../../hooks/useAssetInventoryData';

const ACCIDENT_PHOTO_MAX = 15;
const ACCIDENT_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const ACCIDENT_PHOTOS_TOTAL_BYTES = 75 * 1024 * 1024;

const ACCIDENT_TYPE_OPTIONS = [
  { value: 'head_to_rear', label: 'Collision — Head to Rear' },
  { value: 'chain_collision', label: 'Chain Collision' },
  { value: 'change_or_cross_lane', label: 'Collision — Change / Cross Lane' },
  { value: 'major_minor_road', label: 'Collision — Major / Minor Road' },
  { value: 'opening_door', label: 'Collision — Opening Door of Vehicle' },
  { value: 'collided_with_property', label: 'Collided into Property' },
  { value: 'hit_kerb_or_barrier', label: 'Hit Kerb / Barrier' },
  { value: 'other', label: 'Other' },
];

const INJURY_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'outpatient', label: 'Outpatient' },
  { id: 'hospitalised', label: 'Hospitalised' },
  { id: 'fatality', label: 'Fatality' },
];

const PARTY_TYPE_OPTIONS = [
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'pedestrian', label: 'Pedestrian' },
  { value: 'cyclist', label: 'Cyclist' },
  { value: 'property', label: 'Property' },
];

const STEPS = [
  { id: 1, name: 'Details', icon: AlertCircle },
  { id: 2, name: 'Photos', icon: Camera },
  { id: 3, name: 'Third Party', icon: Users },
];

const emptyForm = (presetVehicleNumber = '') => ({
  vehicle_number: presetVehicleNumber,
  accident_datetime: null,
  location: '',
  accident_type: '',
  description: '',
  injury_level: 'none',
  pedestrian_or_cyclist_involved: false,
  hit_and_run: false,
  foreign_vehicle_involved: false,
  government_property_damaged: false,
  latitude: null,
  longitude: null,
  third_party_type: 'vehicle',
  third_party_name: '',
  third_party_nric_fin: '',
  third_party_phone: '',
  third_party_address: '',
  third_party_vehicle_number: '',
  third_party_insurer_name: '',
  third_party_owner_name: '',
  third_party_owner_address: '',
});

const Field = ({ label, required, hint, children }) => (
  <div className="min-w-0">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-600">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

/**
 * 3-step modal stepper — icon + caption share one column (OvertimeModal
 * pattern). A separate justify-between label row drifts captions off the
 * circles once labels differ in width ("Details" vs "Third Party").
 */
const AccidentStepper = ({ step, theme }) => {
  const active = theme === 'admin' ? 'bg-red-600 text-white shadow-sm' : 'bg-blue-800 text-white shadow-sm';
  const labelActive = theme === 'admin' ? 'text-red-600 font-medium' : 'text-blue-800 font-medium';
  const connectorActive = theme === 'admin' ? 'bg-red-600' : 'bg-blue-800';

  return (
    <div className="mb-4">
      <div className="relative">
        {/* Connectors sit behind the circles, centered on the 40px glyph row */}
        <div className="absolute left-[16.666%] right-[16.666%] top-5 grid grid-cols-2 -translate-y-1/2 pointer-events-none">
          <div
            className={`mx-5 h-1 rounded-full transition-all duration-200 ${
              step > 1 ? connectorActive : 'bg-gray-200'
            }`}
          />
          <div
            className={`mx-5 h-1 rounded-full transition-all duration-200 ${
              step > 2 ? connectorActive : 'bg-gray-200'
            }`}
          />
        </div>

        <div className="relative grid grid-cols-3 gap-2 text-center text-xs">
          {STEPS.map((s) => {
            const Icon = s.icon;
            const done = step > s.id;
            const current = step === s.id;
            return (
              <div key={s.id} className="flex flex-col items-center min-w-0">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
                    done || current ? active : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {done ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span
                  className={`mt-2 leading-tight transition-colors duration-200 ${
                    done || current ? labelActive : 'text-gray-500'
                  }`}
                >
                  {s.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const BoolCheck = ({ id, checked, onChange, label, theme }) => {
  const ring = theme === 'admin' ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-800';
  return (
    <label
      htmlFor={id}
      className={`flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 cursor-pointer hover:bg-gray-50 focus-within:ring-2 ${ring}`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-current"
      />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
};

const ReportAccidentModal = ({
  isOpen,
  onClose,
  theme = 'employee',
  presetVehicleNumber = '',
}) => {
  const isAdmin = theme === 'admin';
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => emptyForm(presetVehicleNumber));
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [locationFromGps, setLocationFromGps] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const locationRef = useRef('');

  const { data: vehicles = [] } = useFleetVehicles({ page_size: 500 });
  const { position, error: geoError, loading: geoLoading, requestPosition } = useCurrentPosition();

  const reportAdmin = useReportAccident();
  const reportMine = useReportMyAccident();
  const addPhotosAdmin = useAddAccidentPhotos();
  const addPhotosMine = useUploadMyAccidentPhotos();
  const createMutation = isAdmin ? reportAdmin : reportMine;
  const photosMutation = isAdmin ? addPhotosAdmin : addPhotosMine;

  const policeTriggers = useMemo(() => ({
    injury_level: form.injury_level,
    pedestrian_or_cyclist_involved: form.pedestrian_or_cyclist_involved,
    hit_and_run: form.hit_and_run,
    foreign_vehicle_involved: form.foreign_vehicle_involved,
    government_property_damaged: form.government_property_damaged,
  }), [
    form.injury_level,
    form.pedestrian_or_cyclist_involved,
    form.hit_and_run,
    form.foreign_vehicle_involved,
    form.government_property_damaged,
  ]);

  const { data: policeVerdict } = useDerivePoliceRequired(policeTriggers, {
    enabled: isOpen,
  });

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm(presetVehicleNumber));
      setPhotos([]);
      setStep(1);
      setSubmitting(false);
      setLocationFromGps(false);
      setReverseLoading(false);
    }
  }, [isOpen, presetVehicleNumber]);

  useEffect(() => {
    locationRef.current = form.location;
  }, [form.location]);

  // GPS pin first; reverse-geocode only suggests Location when the field is empty.
  useEffect(() => {
    if (!position) return;

    const lat = Number(position.coords.latitude.toFixed(6));
    const lng = Number(position.coords.longitude.toFixed(6));

    setForm((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));

    if (locationRef.current.trim()) return undefined;

    let cancelled = false;
    (async () => {
      setReverseLoading(true);
      try {
        const address = await GeocodingService.reverseGeocode(lat, lng);
        if (cancelled || !address || locationRef.current.trim()) return;
        locationRef.current = address;
        setLocationFromGps(true);
        setForm((prev) => (
          prev.location.trim() ? prev : { ...prev, location: address }
        ));
      } finally {
        if (!cancelled) setReverseLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [position]);

  const set = (key, value) => {
    if (key === 'location') setLocationFromGps(false);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const inputClass = isAdmin ? 'input-admin w-full' : 'input w-full';
  const textareaClass = isAdmin ? 'textarea-admin w-full' : 'textarea w-full';
  const primaryBtn = isAdmin ? 'btn btn-admin-primary' : 'btn btn-primary';
  const outlineBtn = isAdmin ? 'btn btn-admin-outline' : 'btn btn-outline';

  const canProceedStep1 = Boolean(
    form.vehicle_number
    && form.accident_datetime
    && form.accident_type
  );

  const resetAndClose = () => {
    setForm(emptyForm(presetVehicleNumber));
    setPhotos([]);
    setStep(1);
    setSubmitting(false);
    onClose();
  };

  const buildPayload = (includeThirdParty) => {
    const payload = {
      vehicle_number: form.vehicle_number,
      accident_datetime: form.accident_datetime.toISOString(),
      location: form.location.trim(),
      accident_type: form.accident_type,
      description: form.description.trim(),
      injury_level: form.injury_level,
      pedestrian_or_cyclist_involved: form.pedestrian_or_cyclist_involved,
      hit_and_run: form.hit_and_run,
      foreign_vehicle_involved: form.foreign_vehicle_involved,
      government_property_damaged: form.government_property_damaged,
    };
    if (form.latitude != null) payload.latitude = form.latitude;
    if (form.longitude != null) payload.longitude = form.longitude;

    if (includeThirdParty) {
      const hasTp = [
        form.third_party_name,
        form.third_party_nric_fin,
        form.third_party_phone,
        form.third_party_vehicle_number,
        form.third_party_insurer_name,
      ].some((v) => String(v || '').trim());
      if (hasTp) {
        payload.third_party_type = form.third_party_type;
        payload.third_party_name = form.third_party_name.trim();
        payload.third_party_nric_fin = form.third_party_nric_fin.trim();
        payload.third_party_phone = form.third_party_phone.trim();
        payload.third_party_address = form.third_party_address.trim();
        payload.third_party_vehicle_number = form.third_party_vehicle_number.trim();
        payload.third_party_insurer_name = form.third_party_insurer_name.trim();
        payload.third_party_owner_name = form.third_party_owner_name.trim();
        payload.third_party_owner_address = form.third_party_owner_address.trim();
      }
    }
    return payload;
  };

  const uploadPhotosIfAny = async (accidentId) => {
    if (!photos.length) return;
    const photoError = validateReturnPhotosForSubmit(photos, {
      maxPhotos: ACCIDENT_PHOTO_MAX,
      maxBytes: ACCIDENT_PHOTO_MAX_BYTES,
      totalBytes: ACCIDENT_PHOTOS_TOTAL_BYTES,
    });
    if (photoError) {
      toast.warning(`Accident saved, but photos were skipped: ${photoError}`);
      return;
    }
    try {
      if (isAdmin) {
        await photosMutation.mutateAsync({ id: accidentId, photos });
      } else {
        await photosMutation.mutateAsync({ accidentId, photos });
      }
    } catch {
      // Mutation toasts the error; record already exists.
    }
  };

  const submit = async (includeThirdParty) => {
    if (!canProceedStep1 || submitting) return;
    setSubmitting(true);
    try {
      const accident = await createMutation.mutateAsync(buildPayload(includeThirdParty));
      await uploadPhotosIfAny(accident.id);
      resetAndClose();
    } catch {
      // createMutation already toasted
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrimary = () => {
    if (step < 3) {
      setStep((s) => s + 1);
      return;
    }
    submit(true);
  };

  const handleSkip = () => {
    if (step === 1) {
      if (!canProceedStep1) {
        toast.error('Vehicle, date/time and accident type are required.');
        return;
      }
      submit(false);
      return;
    }
    if (step === 2) {
      setStep(3);
      return;
    }
    submit(false);
  };

  const busy = submitting || createMutation.isPending || photosMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title="Report Vehicle Accident"
      size="lg"
      theme={theme}
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={resetAndClose} className={outlineBtn} disabled={busy}>
            Cancel
          </button>
          {step >= 1 && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={!canProceedStep1 || busy}
              className={outlineBtn}
            >
              {step === 3 ? 'Skip & Submit' : step === 1 ? 'Skip & Submit' : 'Skip'}
            </button>
          )}
          <button
            type="button"
            onClick={handlePrimary}
            disabled={!canProceedStep1 || busy}
            className={primaryBtn}
          >
            <Car className="w-4 h-4 mr-2" />
            {busy ? 'Submitting...' : step === 3 ? 'Submit Accident' : 'Continue'}
          </button>
        </>
      }
    >
      <AccidentStepper step={step} theme={theme} />

      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Vehicle" required hint="Not in the list? Type the plate as printed.">
              <SearchableCombobox
                items={vehicles}
                value={form.vehicle_number}
                onChange={(value) => set('vehicle_number', value)}
                displayFn={(v) => v.vehicle_number}
                valueFn={(v) => v.vehicle_number}
                searchFn={(v, term) => v.vehicle_number?.toLowerCase().includes(term)}
                placeholder="Select vehicle..."
                searchPlaceholder="Search plate number..."
                variant="form"
                theme={theme}
                className="w-full"
              />
            </Field>

            <Field label="Accident date & time" required>
              {/* DatePickerField owns theme input class + icon pl-12 — do not
                  pass className or the leading icon will sit on the placeholder. */}
              <DatePickerField
                selected={form.accident_datetime}
                onChange={(date) => set('accident_datetime', date)}
                showTimeSelect
                timeIntervals={15}
                dateFormat={DATE_PICKER_DATETIME_FORMAT}
                maxDate={new Date()}
                placeholderText="Select date & time"
                theme={theme}
              />
            </Field>

            <Field label="Accident type" required>
              <FormSelect
                options={ACCIDENT_TYPE_OPTIONS}
                value={form.accident_type}
                onChange={(value) => set('accident_type', value)}
                placeholder="Select type..."
                theme={theme}
              />
            </Field>

            <Field
              label="Location"
              hint={locationFromGps ? 'Suggested from GPS — edit if needed.' : undefined}
            >
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => set('location', e.target.value)}
                  placeholder="e.g. CTE towards SLE, near Exit 6"
                  className={inputClass}
                />
                <button
                  type="button"
                  onClick={() => requestPosition()}
                  disabled={geoLoading || reverseLoading}
                  className={`${isAdmin ? 'icon-button-admin' : 'icon-button'} flex-shrink-0 disabled:opacity-50`}
                  title="Use my location"
                  aria-label="Use my location"
                >
                  <Navigation className={`w-4 h-4 ${(geoLoading || reverseLoading) ? 'animate-pulse' : ''}`} />
                </button>
              </div>
              {(form.latitude != null || geoError) && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  {geoError
                    || (reverseLoading
                      ? `${form.latitude}, ${form.longitude} · looking up address…`
                      : `${form.latitude}, ${form.longitude}`)}
                </p>
              )}
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              placeholder="What happened, in a few sentences"
              className={textareaClass}
            />
          </Field>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Injury level</p>
            <PillToggle
              value={form.injury_level}
              onChange={(value) => set('injury_level', value)}
              options={INJURY_OPTIONS}
              theme={theme}
              fullWidth
              ariaLabel="Injury level"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <BoolCheck
              id="acc-ped"
              checked={form.pedestrian_or_cyclist_involved}
              onChange={(v) => set('pedestrian_or_cyclist_involved', v)}
              label="Pedestrian or cyclist involved"
              theme={theme}
            />
            <BoolCheck
              id="acc-hitrun"
              checked={form.hit_and_run}
              onChange={(v) => set('hit_and_run', v)}
              label="Hit and run"
              theme={theme}
            />
            <BoolCheck
              id="acc-foreign"
              checked={form.foreign_vehicle_involved}
              onChange={(v) => set('foreign_vehicle_involved', v)}
              label="Foreign vehicle involved"
              theme={theme}
            />
            <BoolCheck
              id="acc-gov"
              checked={form.government_property_damaged}
              onChange={(v) => set('government_property_damaged', v)}
              label="Government property damaged"
              theme={theme}
            />
          </div>

          {policeVerdict && (
            <div
              className={`rounded-xl border px-3 py-2.5 flex items-start gap-2 ${
                policeVerdict.police_report_required
                  ? 'bg-red-50 border-red-200 text-red-900'
                  : 'bg-green-50 border-green-200 text-green-900'
              }`}
            >
              {policeVerdict.police_report_required
                ? <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                : <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {policeVerdict.police_report_required
                    ? 'Traffic Police report required'
                    : 'No Traffic Police report required'}
                </p>
                <p className="text-xs opacity-90 mt-0.5">
                  {policeVerdict.police_report_required
                    ? 'Lodge a TP report from these answers, then record the reference later. You still need to report to the insurer within 24 hours or by the next working day.'
                    : 'No TP report needed for these answers. You still need to report to the insurer within 24 hours or by the next working day.'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 flex items-start gap-1.5">
            <FileText className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-400" />
            Scene and damage photos help the insurer. You can skip and add them later.
          </p>
          <ReturnPhotoUploader
            photos={photos}
            onChange={setPhotos}
            label="Scene photos"
            maxPhotos={ACCIDENT_PHOTO_MAX}
            maxBytes={ACCIDENT_PHOTO_MAX_BYTES}
            totalBytes={ACCIDENT_PHOTOS_TOTAL_BYTES}
            theme={theme}
            helpText={`Up to ${ACCIDENT_PHOTO_MAX} images, 5MB each. JPEG, PNG, WebP.`}
          />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Optional — fill in what you know about the other party. Skip if none, or if you
            will add this later.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Party type">
              <FormSelect
                options={PARTY_TYPE_OPTIONS}
                value={form.third_party_type}
                onChange={(value) => set('third_party_type', value)}
                theme={theme}
              />
            </Field>
            <Field label="Name">
              <input
                type="text"
                value={form.third_party_name}
                onChange={(e) => set('third_party_name', e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="NRIC / FIN">
              <input
                type="text"
                value={form.third_party_nric_fin}
                onChange={(e) => set('third_party_nric_fin', e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Phone">
              <input
                type="text"
                value={form.third_party_phone}
                onChange={(e) => set('third_party_phone', e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Vehicle number">
              <input
                type="text"
                value={form.third_party_vehicle_number}
                onChange={(e) => set('third_party_vehicle_number', e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Insurer">
              <input
                type="text"
                value={form.third_party_insurer_name}
                onChange={(e) => set('third_party_insurer_name', e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Address" hint="Optional">
              <input
                type="text"
                value={form.third_party_address}
                onChange={(e) => set('third_party_address', e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Owner name" hint="If different from driver">
              <input
                type="text"
                value={form.third_party_owner_name}
                onChange={(e) => set('third_party_owner_name', e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ReportAccidentModal;
